import { acceptHMRUpdate, defineStore } from 'pinia'

import api from '@/lib/api'
import { transformApiErrorsToString, transformDeliveryContact } from '@/lib/utils/transformEntries'

import AnalyticsService from '@/services/AnalyticsService'

import app from '@/app'
import { useCartFlowStore } from '@/stores/cart-flow'
import { useProductStore } from '@/stores/product'
import { useSessionStore } from '@/stores/session'
import {
  AlgoliaInventoryProduct,
  ApiAddProductToCartPayload,
  ApiAddProductToCartResponse,
  ApiAddress,
  ApiCartItem,
  ApiPriceField,
  CartGuestAddress,
  CartItem,
  DeliveryContact,
} from '@/types'

type State = {
  products: CartItem[]
  contactInformation: CartGuestAddress | object
  shippingInformation: ApiAddress | object
  billingInformation: ApiAddress | object
  poNumber: string
  paymentMethod: string
  cartNumber: string
  referenceNumbers: string[]
  additionalInformation: string
  shippingNotificationEmails: string[]
  billingNotificationEmails: string[]
  summary: {
    deliveryCost: ApiPriceField
    packagingCost: ApiPriceField
    subTotal: ApiPriceField
    totalDiscounts: ApiPriceField
    totalPromotionDiscounts: ApiPriceField
    totalPrice: ApiPriceField
    totalPriceWithTax: ApiPriceField
    totalTax: ApiPriceField
    taxError: string
    productsQty: number
    pricedProductsQty: number
    nonPricedProductsQty: number
  }
  cartEditableEntry: Record<string, string>
  deliveryContact: DeliveryContact | object
  toClear: boolean
  inventoryProducts: CartItem[]
  promo: string | null
  promoError: string
  isPlaceQuoteAllowed: boolean
  submitAsTwo: boolean
  isContactFormValid: boolean
  isSubmitRegistration: boolean
  registrationData: Record<string, string>
  registrationCompletedData: Record<string, string>
  isCreditCardAllowed: boolean
  isPoAllowed: boolean
}

type Actions = {
  getCart: (recalculate?: boolean) => Promise<void>
  clearCart: (userId?: string) => Promise<void>
  initCartState: () => void
  resetCartStep: () => void
  setContactInformation: (contactInformation: CartGuestAddress) => void
  setShippingInformation: ({ addressData }: { addressData: ApiAddress }) => Promise<void>
  setBillingInformation: ({ addressData }: { addressData: ApiAddress }) => Promise<void>
  setAdditionalInformation: (additionalInformation: string) => void
  addShippingNotificationEmail: (email: string) => Promise<void>
  deleteShippingNotificationEmail: (email: string) => Promise<void>
  addBillingNotificationEmail: (email: string) => Promise<void>
  deleteBillingNotificationEmail: (email: string) => Promise<void>
  setProductToUpdate: (cartEditableEntry: Record<string, string>) => void
  clearUpdateProduct: () => void
  setDeliveryContact: (deliveryContact: Record<string, string>) => Promise<void>
  setProductShippingAddress: ({
    entryNumber,
    shippingAddress,
  }: {
    entryNumber: number
    shippingAddress: ApiAddress
  }) => Promise<void>
  deleteProductShippingAddress: ({
    entryNumber,
    shippingAddress,
  }: {
    entryNumber: string
    shippingAddress: Record<string, string>
  }) => Promise<void>
  updateProductDeliveryContact: ({
    entryNumber,
    deliveryContact,
  }: {
    entryNumber: string
    deliveryContact: Record<string, string>
  }) => Promise<void>
  addProductsToCart: (
    productsToAdd: ApiAddProductToCartPayload[],
  ) => Promise<ApiAddProductToCartResponse>
  updateCartProduct: ({
    entryNumber,
    productData,
  }: {
    entryNumber: string
    productData: {
      quantity: number
      customizations: {
        title: string
        selected: string
        skuAttribute: boolean
      }[]
      code: string
      pricingParams: {
        salesToStock: number
        aliqPerLot: number
        volPerAliq: number
      }
      productImageUrl: string
      productUrl: string
      zeroPriceEntry: boolean
      reservedOrderLine?: boolean
      createNew?: boolean
    }
  }) => Promise<void>
  deleteProductsFromCart: (entriesToDelete: Record<number, object>) => Promise<void>
  duplicateCartProduct: (entryNumber: number) => Promise<void>
  submitQuote: (payload?: {
    address?: CartGuestAddress | object
    quoteCartReminders?: boolean
    syncMode?: boolean
  }) => Promise<void>
  setPromo: (data: { promoCode: string }) => Promise<void>
  setIsContactFormValid: (isContactFormValid: boolean) => void
  setIsSubmitRegistration: (isSubmitRegistration: boolean) => void
  setRegistrationData: (registrationData: Record<string, string>) => void
  setRegistrationCompletedData: (registrationCompletedData: Record<string, string>) => void
  setSubmitAsTwo: (submitAsTwo: boolean) => void
}

type Getters = {
  productsQty: (state: State) => number
  pricedProducts: (state: State) => ApiCartItem[]
  nonPricedProducts: (state: State) => ApiCartItem[]
  isPaymentAllowed: () => boolean
  isCartGuestView: () => boolean
}

export const useCartStore = defineStore<string, State, Getters, Actions>('cart', {
  state: () => ({
    products: [],
    contactInformation: {},
    shippingInformation: {},
    billingInformation: {},
    poNumber: '',
    paymentMethod: '',
    cartNumber: '',
    referenceNumbers: [],
    additionalInformation: '',
    shippingNotificationEmails: [],
    billingNotificationEmails: [],
    summary: {
      deliveryCost: {
        currencyIso: '',
        value: 0,
        formattedValue: '',
      },
      packagingCost: {
        currencyIso: '',
        value: 0,
        formattedValue: '',
      },
      subTotal: {
        currencyIso: '',
        value: 0,
        formattedValue: '',
      },
      totalDiscounts: {
        currencyIso: '',
        value: 0,
        formattedValue: '',
      },
      totalPromotionDiscounts: {
        currencyIso: '',
        value: 0,
        formattedValue: '',
      },
      totalPrice: {
        currencyIso: '',
        value: 0,
        formattedValue: '',
      },
      totalPriceWithTax: {
        currencyIso: '',
        value: 0,
        formattedValue: '',
      },
      totalTax: {
        currencyIso: '',
        value: 0,
        formattedValue: '',
      },
      taxError: '',
      productsQty: 0,
      pricedProductsQty: 0,
      nonPricedProductsQty: 0,
    },
    cartEditableEntry: {},
    deliveryContact: {},
    toClear: false,
    inventoryProducts: [],
    promo: null,
    promoError: '',
    isPlaceQuoteAllowed: true,
    submitAsTwo: false,
    isContactFormValid: false,
    isSubmitRegistration: false,
    registrationData: {},
    registrationCompletedData: {},
    isCreditCardAllowed: false,
    isPoAllowed: false,
  }),

  getters: {
    productsQty: (state) => state.products.length,
    pricedProducts: (state) => {
      return state.products.filter((el) => el.totalNetPrice?.value)
    },
    nonPricedProducts: (state) => {
      return state.products.filter((el) => !el.totalNetPrice?.value)
    },
    isPaymentAllowed() {
      const sessionStore = useSessionStore()
      return (
        !this.nonPricedProducts.length &&
        !sessionStore.user.isUserProspect &&
        (this.isCreditCardAllowed || this.isPoAllowed)
      )
    },
    isCartGuestView: () => {
      const sessionStore = useSessionStore()
      return !sessionStore.isUserLoggedIn || sessionStore.user.isUserProspect
    },
  },

  actions: {
    async getCart(recalculate = true) {
      const sessionStore = useSessionStore()
      const isLoggedIn = sessionStore.isUserLoggedIn

      const { data } = await api.cart.getCart({ recalculate })
      const isContainsInventory = data.entries?.some((entry) => entry.inventory?.length)
      let inventoryData: Record<string, AlgoliaInventoryProduct> = {}

      if (isContainsInventory) {
        const loadInventoryData = async () => {
          const toLoad = data.entries.reduce((sum, curr) => {
            if (curr.inventory?.length) {
              sum.push(...curr.inventory.map((el) => el.objectID))
            }
            return sum
          }, [] as string[])
          const { hits } = await app.config.globalProperties.$search.service
            .getInventoryIndex()
            .search<AlgoliaInventoryProduct>('', {
              hitsPerPage: 1000,
              filters: toLoad.map((el) => `objectID:${el}`).join(' OR '),
              clickAnalytics: false,
            })
          return hits.reduce(
            (sum, curr) => {
              sum[curr.objectID] = curr
              return sum
            },
            {} as Record<string, AlgoliaInventoryProduct>,
          )
        }
        inventoryData = await loadInventoryData()
      }

      this.cartNumber = data.id
      const products: CartItem[] = (data.entries?.map((el) => {
        let rollQuantity
        const isReserveProduct = !!el.reservedOrderLine
        if (!isReserveProduct && el.inventory) {
          rollQuantity = el.inventory.find((el) => inventoryData[el.objectID])?.rollQuantity
        }
        return {
          ...el,
          deliveryContact: el.deliveryContact.uid
            ? transformDeliveryContact(el.deliveryContact)
            : null,
          customizations: el.customizations || [],
          rollQuantity,
        }
      }) || []) as CartItem[]
      this.products = products

      if (isLoggedIn) {
        this.billingInformation = data.billingAddress || {}
        this.shippingInformation = data.deliveryAddress || {}
        this.paymentMethod = data.paymentType.code
        this.poNumber = data.poNumber || ''
        this.shippingNotificationEmails = data.deliveryNotificationEmails
        this.billingNotificationEmails = data.billingNotificationEmails
        this.deliveryContact = data.deliveryContact.uid
          ? transformDeliveryContact(data.deliveryContact)
          : {}
        this.promo = data.promoCode || null
        this.summary = {
          deliveryCost: data.deliveryCost || {},
          packagingCost: data.packagingCost || {},
          subTotal: data.subTotal,
          totalDiscounts: data.totalDiscounts,
          totalPromotionDiscounts: data.totalPromotionDiscounts,
          totalPrice: data.totalPrice,
          totalPriceWithTax: data.totalPriceWithTax,
          totalTax: data.totalTax,
          taxError: data?.calculationResult?.taxErrorMessage || '',
          productsQty: data.entries.length,
          pricedProductsQty: data.entries.filter((el) => el.itemNetPrice.value).length,
          nonPricedProductsQty: data.entries.filter((el) => !el.itemNetPrice.value).length,
        }
        this.inventoryProducts = products.filter((el) => el.inventory?.some((el) => el.objectID))
        this.promoError =
          data?.calculationResult?.errorMessage || data?.calculationResult?.warningMessage || ''
        this.isPlaceQuoteAllowed = !!(
          data?.calculationResult?.isSuccessful || data?.calculationResult?.isEligibleForQuote
        )
        this.isCreditCardAllowed = data.payable
        this.isPoAllowed = data.poAllowed
      }
    },

    async clearCart(userId) {
      const sessionStore = useSessionStore()
      if (sessionStore.isUserLoggedIn) {
        await api.cart.deleteCart(userId)
      }
      this.$reset()
    },

    initCartState() {
      if (this.toClear) {
        this.$reset()
        const cartFlowStore = useCartFlowStore()
        cartFlowStore.setStep('empty')
      }
    },

    resetCartStep() {
      const cartFlowStore = useCartFlowStore()
      if (!this.productsQty) {
        cartFlowStore.setStep('empty')
      } else {
        cartFlowStore.setStep('view')
      }
    },

    setContactInformation(contactInformation) {
      this.contactInformation = contactInformation || {}
    },

    async setShippingInformation({ addressData }) {
      await api.cart.setCartShippingAddressId({ addressId: addressData.id })
      this.shippingInformation = addressData
      await this.getCart()
    },

    async setBillingInformation({ addressData }) {
      await api.cart.setCartBillingAddressId({ addressId: addressData.id })
      this.billingInformation = addressData
      await this.getCart(false)
    },

    setAdditionalInformation(additionalInformation) {
      this.additionalInformation = additionalInformation
    },

    async addShippingNotificationEmail(email) {
      const isDuplicate = this.shippingNotificationEmails.some((el) => el === email)
      if (!email || isDuplicate) return
      const newEmailsList = [...this.shippingNotificationEmails, email]
      await api.cart.updateShippingNotificationEmails(newEmailsList)
      await this.getCart(false)
    },

    async deleteShippingNotificationEmail(email) {
      if (!email) return
      const newEmailsList = this.shippingNotificationEmails.filter((el) => el !== email)
      await api.cart.updateShippingNotificationEmails(newEmailsList)
      await this.getCart(false)
    },

    async addBillingNotificationEmail(email) {
      const isDuplicate = this.billingNotificationEmails.some((el) => el === email)
      if (!email || isDuplicate) return
      const newEmailsList = [...this.billingNotificationEmails, email]
      await api.cart.updateBillingNotificationEmails(newEmailsList)
      await this.getCart(false)
    },

    async deleteBillingNotificationEmail(email) {
      if (!email) return
      const newEmailsList = this.billingNotificationEmails.filter((el) => el !== email)
      await api.cart.updateBillingNotificationEmails(newEmailsList)
      await this.getCart(false)
    },

    setProductToUpdate(cartEditableEntry) {
      this.cartEditableEntry = cartEditableEntry
    },

    clearUpdateProduct() {
      this.cartEditableEntry = {}
    },

    async setDeliveryContact(deliveryContact) {
      await api.cart.setDeliveryContact({ customerUid: deliveryContact.value })
      await this.getCart(false)
    },

    async setProductShippingAddress({ entryNumber, shippingAddress }) {
      await api.cart.setProductShippingAddress(entryNumber, {
        addressId: shippingAddress.id,
      })
      await this.getCart(false)
    },

    async deleteProductShippingAddress({ entryNumber, shippingAddress }) {
      await api.cart.deleteProductShippingAddress(entryNumber, {
        addressId: shippingAddress.id,
      })
      await this.getCart(false)
    },

    async updateProductDeliveryContact({ entryNumber, deliveryContact }) {
      await api.cart.setProductDeliveryContact(entryNumber, {
        customerUid: deliveryContact.value ? deliveryContact.value : null,
      })
      await this.getCart(false)
    },

    async addProductsToCart(productsToAdd) {
      const { data: responseEntries } = await api.cart.addProducts(productsToAdd)

      const cartFlowStore = useCartFlowStore()
      if (cartFlowStore.step.value === 'empty') {
        cartFlowStore.setStep('view')
      }
      await this.getCart()

      const addedProductEntries = responseEntries.filter(
        (responseEntry) => responseEntry.statusCode === 'success',
      )
      const addedProductCartEntriesData = addedProductEntries
        .map((addedProductEntry) => {
          const cartEntry = this.products.find(
            (cartEntry) => cartEntry.entryNumber === addedProductEntry.entry.entryNumber,
          )
          return cartEntry ? { ...cartEntry, quantityAdded: addedProductEntry.quantityAdded } : null
        })
        .filter((el): el is NonNullable<typeof el> => el !== null)

      if (addedProductCartEntriesData.length) {
        const productStore = useProductStore()
        AnalyticsService.sendAddToCartEvent({
          list: productStore.analyticsData.navigatedFromPageTitle || 'Direct PGP',
          products: addedProductCartEntriesData.map((cartEntry) => ({
            id: cartEntry.sku,
            name: cartEntry.productName,
            category: cartEntry.productType,
            quantity: cartEntry.quantityAdded,
          })),
        })
      }

      return responseEntries
    },

    async updateCartProduct({ entryNumber, productData }) {
      await api.cart.updateProduct(entryNumber, productData)
      await this.getCart()
    },

    async deleteProductsFromCart(entriesToDelete) {
      const cartFlowStore = useCartFlowStore()

      await api.cart.deleteProducts(entriesToDelete)
      await this.getCart()
      if (!this.productsQty) {
        await this.clearCart()
        cartFlowStore.setStep('empty')
      }
    },

    async duplicateCartProduct(entryNumber) {
      await api.cart.duplicateProduct(entryNumber)
      await this.getCart()
    },

    async submitQuote({ address = {}, quoteCartReminders = false, syncMode = false } = {}) {
      const { data } = await api.cart.placeQuote({
        submitAsTwo: this.submitAsTwo,
        quoteCartReminders,
        additionalInformation: this.additionalInformation,
        address,
        syncMode,
      })
      this.referenceNumbers = data.quotes.map((quote) => quote.code)
    },

    async setPromo(data) {
      try {
        await api.cart.setPromo(data)
        await this.getCart()
      } catch (error) {
        this.promoError = transformApiErrorsToString(error)
      }
    },

    setIsContactFormValid(isContactFormValid) {
      this.isContactFormValid = isContactFormValid
    },
    setIsSubmitRegistration(isSubmitRegistration) {
      this.isSubmitRegistration = isSubmitRegistration
    },
    setRegistrationData(registrationData) {
      this.registrationData = registrationData
    },
    setRegistrationCompletedData(registrationCompletedData) {
      this.registrationCompletedData = registrationCompletedData
    },
    setSubmitAsTwo(submitAsTwo) {
      this.submitAsTwo = submitAsTwo
    },
  },
  persist: true,
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCartStore, import.meta.hot))
}
